import React, { useState } from 'react';
import { DB } from '../data/database';
import { exportCSV } from '../data/constants';
import { useLanguage } from '../translations/LanguageContext';
import { SearchIcon } from './Icons';

function formatCur(amount, cur) {
  const symbols = { AED: 'AED', USD: '$', JPY: '¥' };
  return `${symbols[cur] || cur} ${Number(amount).toLocaleString()}`;
}

function YardSalesHistory({ user }) {
  const { t } = useLanguage();
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState('all');

  const allBids = DB.get('yardBids') || [];
  const wonBids = allBids.filter(b => b.status === 'won').sort((a, b) => (b.sale_approved_at || b.created_at || '').localeCompare(a.sale_approved_at || a.created_at || ''));

  // Date filtering
  const now = new Date();
  const today = now.toISOString().split('T')[0];
  const weekAgo = new Date(now - 7 * 86400000).toISOString().split('T')[0];
  const monthAgo = new Date(now - 30 * 86400000).toISOString().split('T')[0];

  let filtered = wonBids;
  if (dateRange === 'today') filtered = wonBids.filter(b => (b.sale_approved_at || b.created_at || '').startsWith(today));
  else if (dateRange === 'week') filtered = wonBids.filter(b => (b.sale_approved_at || b.created_at || '') >= weekAgo);
  else if (dateRange === 'month') filtered = wonBids.filter(b => (b.sale_approved_at || b.created_at || '') >= monthAgo);

  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(b => [b.unit_description, b.unit_sku, b.customer_name, b.customer_phone].filter(Boolean).join(' ').toLowerCase().includes(q));
  }

  // Stats
  const todaySales = wonBids.filter(b => (b.sale_approved_at || b.created_at || '').startsWith(today));
  const weekSales = wonBids.filter(b => (b.sale_approved_at || b.created_at || '') >= weekAgo);
  const monthSales = wonBids.filter(b => (b.sale_approved_at || b.created_at || '') >= monthAgo);
  const sumAed = (arr) => arr.reduce((s, b) => s + (b.currency === 'AED' ? b.amount : (b.amount_usd || 0) * 3.67), 0);

  const handleExport = () => {
    const allU = [...(DB.get('vehicles') || []), ...(DB.get('engines') || []), ...(DB.get('spareParts') || [])];
    const rows = filtered.map(b => {
      const unit = allU.find(u => u.id === b.unit_id);
      return {
        Date: (b.sale_approved_at || b.created_at || '').split('T')[0],
        Unit: b.unit_description || '',
        Chassis: unit?.chassis || '',
        SKU: b.unit_sku || '',
        Customer: b.customer_name || '',
        Phone: b.customer_phone || '',
        Amount: b.amount || 0,
        Currency: b.currency || 'AED',
        USD: b.amount_usd || 0,
        Status: b.payment_status || 'unpaid',
      };
    });
    if (rows.length === 0) { alert('No data'); return; }
    const headers = Object.keys(rows[0]);
    const csv = [headers.join(','), ...rows.map(r => headers.map(h => `"${String(r[h]).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `yard-sales-${new Date().toISOString().slice(0,10)}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ margin: 0 }}>{t('nav_yard_history')}</h2>
        {user.role !== 'yard_sales' && <button className="btn btn-secondary btn-sm" onClick={handleExport}>Export CSV</button>}
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 16 }}>
        <div style={{ padding: 14, background: 'var(--bg3)', borderRadius: 10, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 4 }}>Today</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--green)' }}>{todaySales.length} units</div>
          {user.role !== 'yard_sales' && <div style={{ fontSize: 12, color: 'var(--text2)' }}>AED {Math.round(sumAed(todaySales)).toLocaleString()}</div>}
        </div>
        <div style={{ padding: 14, background: 'var(--bg3)', borderRadius: 10, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 4 }}>This Week</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--blue)' }}>{weekSales.length} units</div>
          {user.role !== 'yard_sales' && <div style={{ fontSize: 12, color: 'var(--text2)' }}>AED {Math.round(sumAed(weekSales)).toLocaleString()}</div>}
        </div>
        <div style={{ padding: 14, background: 'var(--bg3)', borderRadius: 10, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 4 }}>This Month</div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>{monthSales.length} units</div>
          {user.role !== 'yard_sales' && <div style={{ fontSize: 12, color: 'var(--text2)' }}>AED {Math.round(sumAed(monthSales)).toLocaleString()}</div>}
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <select className="inv-filter-select" value={dateRange} onChange={e => setDateRange(e.target.value)}>
          <option value="all">All Time</option>
          <option value="today">Today</option>
          <option value="week">This Week</option>
          <option value="month">This Month</option>
        </select>
        <div className="inv-search-wrap">
          <span className="inv-search-icon"><SearchIcon /></span>
          <input className="inv-search" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: 180 }} />
        </div>
        <span style={{ fontSize: 11, color: 'var(--text3)' }}>{filtered.length} sales</span>
      </div>

      {/* Sales table */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
          <div style={{ fontSize: 14 }}>No yard sales yet</div>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
            <thead>
              <tr style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>Date</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>Unit</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>Customer</th>
                <th style={{ padding: '8px 10px', textAlign: 'right' }}>Price</th>
                <th style={{ padding: '8px 10px', textAlign: 'center' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(bid => (
                <tr key={bid.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px', fontSize: 12 }}>{(bid.sale_approved_at || bid.created_at || '').split('T')[0]}</td>
                  <td style={{ padding: '10px' }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{bid.unit_description}</div>
                    <div style={{ fontSize: 10, color: 'var(--cyan)', fontFamily: 'monospace' }}>{bid.unit_sku}</div>
                  </td>
                  <td style={{ padding: '10px' }}>
                    <div style={{ fontSize: 12 }}>{bid.customer_name}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)' }}>{bid.customer_phone}</div>
                  </td>
                  <td style={{ padding: '10px', textAlign: 'right', fontSize: 13, fontWeight: 700 }}>{formatCur(bid.amount, bid.currency)}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>
                    <span className={`status ${bid.payment_status === 'paid' ? 'status-available' : 'status-pending'}`} style={{ fontSize: 9 }}>
                      {bid.payment_status === 'paid' ? 'Paid ✅' : 'Awaiting'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default YardSalesHistory;
